import { authErrorCopy } from '@/lib/authErrorCopy';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import sabotagelogo from '@/assets/sabotage-logo.png';
import { CheckCircle2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { isPasswordStrong, PASSWORD_REQUIREMENTS_TEXT } from '@/lib/validationSchemas';
import { consumeAuthCallback, type AuthCallbackErrorKind } from '@/lib/authCallback';
import { DEFAULT_AUTHED_HOME } from '@/lib/landingAuthCopy';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [ready, setReady] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [errorKind, setErrorKind] = useState<AuthCallbackErrorKind | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Handle every recovery link format via the shared helper, deterministically
    // (no timer, no missed-event race):
    //   ?token_hash=&type=recovery -> verifyOtp (cross-device / webview safe: the fix)
    //   ?code= / #access_token     -> detectSessionInUrl exchanged it at client-init;
    //                                 confirmed via getSession (same-browser only)
    // A late-arriving recovery session is still promoted by the listener below.
    let active = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (active && (session || event === 'PASSWORD_RECOVERY')) {
        setErrorKind(null);
        setInvalidLink(false);
        setReady(true);
      }
    });

    (async () => {
      const result = await consumeAuthCallback();
      if (!active) return;
      if (result.status === 'success') { setReady(true); return; }
      if (result.status === 'error') { setErrorKind(result.errorKind ?? 'invalid'); setInvalidLink(true); return; }
      // 'none': no token in the URL. A recovery session may already exist (same
      // browser, detectSessionInUrl already ran). Check deterministically.
      const { data: { session } } = await supabase.auth.getSession();
      if (!active) return;
      if (session) setReady(true);
      else { setErrorKind('invalid'); setInvalidLink(true); }
    })();

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  // Distinct guidance per failure mode instead of one generic message.
  const errCopy = errorKind === 'expired'
    ? { title: 'Reset Link Expired', body: 'This password reset link has expired. Request a new one from the sign in page.' }
    : errorKind === 'used'
    ? { title: 'Reset Link Already Used', body: 'This reset link has already been used. Request a new one from the sign in page.' }
    : { title: 'Invalid Reset Link', body: 'This reset link is invalid or could not be verified in this browser. Request a new one.' };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (!isPasswordStrong(password)) {
      toast({ title: 'Password too weak', description: PASSWORD_REQUIREMENTS_TEXT, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        const c = authErrorCopy(error, 'update_password');
        console.error('[auth] password update failed:', c.detail);
        toast({ title: c.title, description: c.description, variant: 'destructive' });
      } else {
        setDone(true);
        setTimeout(() => navigate(DEFAULT_AUTHED_HOME, { replace: true }), 2000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-border bg-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-20 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground font-display">
            {done ? 'Password Updated!' : invalidLink ? errCopy.title : 'Set New Password'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {done
              ? 'Redirecting you to the app...'
              : invalidLink
                ? errCopy.body
                : 'Choose a new password for your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            </div>
          ) : invalidLink ? (
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
              <p className="text-sm text-muted-foreground">
                Please request a new password reset link from the sign in page.
              </p>
              <Button asChild className="w-full">
                <Link to="/auth">Back to Sign In</Link>
              </Button>
            </div>
          ) : !ready ? (
            <div className="text-center text-muted-foreground text-sm py-4">
              Verifying reset link...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={10}
                    className="bg-input border-border text-foreground pr-10"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{PASSWORD_REQUIREMENTS_TEXT}</p>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-foreground">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirm"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    minLength={10}
                    className="bg-input border-border text-foreground pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
